import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ClipboardList, Plus, Edit2, Archive, Copy, X, Save, Shield } from "lucide-react";

const SEV_CLASS = {
  Critical: "bg-red-100 text-red-700 border-red-200",
  High: "bg-orange-100 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};
const TYPE_CLASS = {
  Favorable: "text-green-700 bg-green-50 border-green-200",
  Unfavorable: "text-red-700 bg-red-50 border-red-200",
  "Required Clause": "text-blue-700 bg-blue-50 border-blue-200",
  "Prohibited Clause": "text-purple-700 bg-purple-50 border-purple-200",
};

export default function EvaluationRulesAdmin() {
  const { user } = useAuth();
  const canManage = user && (user.role === "it_admin" || user.role === "general_counsel");
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ categories: [], rule_types: [], severities: [] });
  const [settings, setSettings] = useState({ require_accepted_evaluation: false, rule_library_version: 0 });
  const [inactive, setInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [filterType, setFilterType] = useState("");

  const load = () => {
    api.get(`/eval-rules?include_inactive=${inactive}`).then(({ data }) => setRules(data || []));
    api.get("/eval-rules/categories").then(({ data }) => setMeta(data));
    api.get("/eval-settings").then(({ data }) => setSettings(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [inactive]);

  const toggleGate = async () => {
    if (user?.role !== "it_admin") return;
    try {
      const { data } = await api.patch("/eval-settings", { require_accepted_evaluation: !settings.require_accepted_evaluation });
      setSettings(s => ({ ...s, require_accepted_evaluation: data.require_accepted_evaluation }));
      toast.success("Setting updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive rule "${r.name}"?`)) return;
    try { await api.delete(`/eval-rules/${r.id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const duplicate = (r) => setEditing({ ...r, id: undefined, new: true, name: `${r.name} (copy)`, sort_order: (r.sort_order || 100) + 5 });

  const filtered = rules.filter(r => (!filterCat || r.category === filterCat) && (!filterType || r.rule_type === filterType));

  return (
    <div className="p-6" data-testid="eval-rules-page">
      <div className="mb-6 flex items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Administration · Contract Evaluator</div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#005289]"/> Evaluation Rules
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Rule library used by the Contract Evaluator. General Counsel + IT Admin manage rules; Attorneys have view-only access.
            Library version <span className="font-mono">v{settings.rule_library_version}</span>.
          </p>
        </div>
        {canManage ? (
          <button onClick={() => setEditing({ new: true, name: "", category: "Other", rule_type: "Unfavorable", condition_field: "", operator: "contains", value: "", unit: "", severity: "Medium", weight: 5, explanation: "", preferred_position: "", active: true, sort_order: 1000 })}
                  data-testid="er-add" className="ml-auto h-9 px-3 text-xs font-semibold bg-[#005289] text-white rounded flex items-center gap-1 hover:opacity-90">
            <Plus className="w-3 h-3"/> Add Rule
          </button>
        ) : null}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4 flex items-center gap-3">
        <Shield className={`w-4 h-4 ${settings.require_accepted_evaluation ? "text-green-600" : "text-slate-400"}`}/>
        <div className="text-xs">
          <div className="font-bold text-slate-900">Approval gate</div>
          <div className="text-slate-500">Require an Accepted evaluation before final approval — currently <span className={`font-bold ${settings.require_accepted_evaluation ? "text-green-700" : "text-slate-500"}`}>{settings.require_accepted_evaluation ? "ON" : "OFF"}</span>.</div>
        </div>
        {user?.role === "it_admin" ? (
          <button onClick={toggleGate} data-testid="er-gate-toggle" className={`ml-auto h-7 px-3 text-xs font-semibold rounded ${settings.require_accepted_evaluation ? "bg-green-600 text-white" : "bg-slate-200 text-slate-700"}`}>
            {settings.require_accepted_evaluation ? "Disable" : "Enable"}
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-2 mb-3 text-xs">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="h-8 px-2 border border-slate-200 rounded" data-testid="er-filter-cat">
          <option value="">All categories</option>
          {meta.categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="h-8 px-2 border border-slate-200 rounded" data-testid="er-filter-type">
          <option value="">All types</option>
          {meta.rule_types.map(c => <option key={c}>{c}</option>)}
        </select>
        <label className="ml-auto flex items-center gap-1"><input type="checkbox" checked={inactive} onChange={e => setInactive(e.target.checked)}/> Show archived</label>
        <span className="text-slate-500">{filtered.length} rule{filtered.length !== 1 && "s"}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-16">Order</th>
              <th className="text-left px-3 py-2">Rule</th>
              <th className="text-left px-3 py-2 w-32">Type</th>
              <th className="text-left px-3 py-2 w-32">Category</th>
              <th className="text-left px-3 py-2 w-28">Severity</th>
              <th className="text-right px-3 py-2 w-16">Wt</th>
              <th className="text-right px-3 py-2 w-40">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="er-rows">
            {filtered.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-400">No rules.</td></tr> : null}
            {filtered.map(r => (
              <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 ${r.active ? "" : "opacity-50"}`} data-testid={`er-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.sort_order}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.condition_field} {r.operator} {r.value}{r.unit ? ` ${r.unit}` : ""}</div>
                </td>
                <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 border rounded ${TYPE_CLASS[r.rule_type] || ""}`}>{r.rule_type}</span></td>
                <td className="px-3 py-2 text-xs">{r.category}</td>
                <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 border rounded ${SEV_CLASS[r.severity] || ""}`}>{r.severity}</span></td>
                <td className="px-3 py-2 text-right font-mono">{r.weight}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {canManage ? (
                    <>
                      <button onClick={() => duplicate(r)} title="Duplicate" data-testid={`er-dup-${r.id}`} className="h-7 px-2 text-xs border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1 mr-1"><Copy className="w-3 h-3"/></button>
                      <button onClick={() => setEditing(r)} data-testid={`er-edit-${r.id}`} className="h-7 px-2 text-xs border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1 mr-1"><Edit2 className="w-3 h-3"/> Edit</button>
                      {r.active ? <button onClick={() => archive(r)} data-testid={`er-arch-${r.id}`} className="h-7 px-2 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1"><Archive className="w-3 h-3"/></button> : null}
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? <RuleDialog rule={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/> : null}
    </div>
  );
}

function RuleDialog({ rule, meta, onClose, onSaved }) {
  const isNew = !!rule.new;
  const [f, setF] = useState({
    name: rule.name || "", category: rule.category || "Other", rule_type: rule.rule_type || "Unfavorable",
    condition_field: rule.condition_field || "", operator: rule.operator || "contains",
    value: rule.value ?? "", unit: rule.unit || "", severity: rule.severity || "Medium",
    weight: rule.weight ?? 5, explanation: rule.explanation || "",
    preferred_position: rule.preferred_position || "", sort_order: rule.sort_order ?? 1000,
    active: rule.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = { ...f, weight: parseInt(f.weight) || 5, sort_order: parseInt(f.sort_order) || 1000 };
      if (isNew) { await api.post("/eval-rules", body); toast.success("Created"); }
      else       { await api.patch(`/eval-rules/${rule.id}`, body); toast.success("Updated"); }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-md w-[640px] max-w-[92vw] max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#005289]"/>
          <div className="font-bold">{isNew ? "New rule" : "Edit rule"}</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Rule name *</span>
            <input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="er-dlg-name" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/>
          </label>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label><span className="font-bold text-slate-700 block mb-1">Category</span>
              <select value={f.category} onChange={e => setF({...f, category: e.target.value})} className="w-full h-9 px-2 border border-slate-200 rounded-md">
                {(meta.categories || []).map(c => <option key={c}>{c}</option>)}
              </select></label>
            <label><span className="font-bold text-slate-700 block mb-1">Rule type</span>
              <select value={f.rule_type} onChange={e => setF({...f, rule_type: e.target.value})} className="w-full h-9 px-2 border border-slate-200 rounded-md">
                {(meta.rule_types || []).map(c => <option key={c}>{c}</option>)}
              </select></label>
            <label><span className="font-bold text-slate-700 block mb-1">Condition field</span>
              <input value={f.condition_field} onChange={e => setF({...f, condition_field: e.target.value})} placeholder="e.g., Term Length" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/></label>
            <label><span className="font-bold text-slate-700 block mb-1">Operator</span>
              <select value={f.operator} onChange={e => setF({...f, operator: e.target.value})} className="w-full h-9 px-2 border border-slate-200 rounded-md">
                {["greater than","less than","equals","not equals","contains","does not contain","is present","is missing","is outside of"].map(o => <option key={o}>{o}</option>)}
              </select></label>
            <label><span className="font-bold text-slate-700 block mb-1">Value / Threshold</span>
              <input value={f.value ?? ""} onChange={e => setF({...f, value: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/></label>
            <label><span className="font-bold text-slate-700 block mb-1">Unit</span>
              <input value={f.unit} onChange={e => setF({...f, unit: e.target.value})} placeholder="years, %, $, days" className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/></label>
            <label><span className="font-bold text-slate-700 block mb-1">Severity</span>
              <select value={f.severity} onChange={e => setF({...f, severity: e.target.value})} className="w-full h-9 px-2 border border-slate-200 rounded-md">
                {(meta.severities || []).map(c => <option key={c}>{c}</option>)}
              </select></label>
            <label><span className="font-bold text-slate-700 block mb-1">Weight (1-10)</span>
              <input type="number" min="1" max="10" value={f.weight} onChange={e => setF({...f, weight: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/></label>
            <label><span className="font-bold text-slate-700 block mb-1">Sort order</span>
              <input type="number" value={f.sort_order} onChange={e => setF({...f, sort_order: e.target.value})} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md"/></label>
          </div>
          <label className="text-xs block"><span className="font-bold text-slate-700 block mb-1">Why this matters (Explanation)</span>
            <textarea rows={2} value={f.explanation} onChange={e => setF({...f, explanation: e.target.value})} className="w-full p-2 text-sm border border-slate-200 rounded-md"/></label>
          <label className="text-xs block"><span className="font-bold text-slate-700 block mb-1">Preferred position / suggested redline</span>
            <textarea rows={3} value={f.preferred_position} onChange={e => setF({...f, preferred_position: e.target.value})} className="w-full p-2 text-sm border border-slate-200 rounded-md"/></label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="er-dlg-save" className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-60"><Save className="w-3 h-3"/> {busy ? "Saving..." : (isNew ? "Create" : "Update")}</button>
        </div>
      </div>
    </div>
  );
}
